import { Loader2Icon } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  useCreateSquareSavedCard,
  useGetSquareSetupConfig,
} from '~entities/payment/api/square-payment-methods';

import { reportError } from '~shared/observability/report-error';
import { getErrorMessage } from '~shared/utils/error-handler';
import {
  buildSellerKeyedStoreVerificationDetails,
  loadSquareWebPaymentsSdk,
  type SquareCard,
} from '~shared/lib/square-web-payments';
import { Button } from '~shared/shadcn/ui/button';
import { DialogFooter } from '~shared/shadcn/ui/dialog';

export const SquareSaveParentCardForm = ({
  parentId,
  parentEmail,
  onClose,
  onSuccess,
}: {
  parentId: string;
  parentEmail?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const cardContainerId = useId().replace(/:/g, '');
  const cardRef = useRef<SquareCard | null>(null);
  const submitLockRef = useRef(false);
  const [isCardReady, setIsCardReady] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const setupQuery = useGetSquareSetupConfig(true);
  const createCard = useCreateSquareSavedCard();
  const squareConfig = setupQuery.data?.square;

  useEffect(() => {
    if (!squareConfig) {
      return;
    }

    let cancelled = false;
    const { applicationId, locationId, sandbox } = squareConfig;

    const attachCard = async () => {
      setLoadError(null);
      setIsCardReady(false);
      try {
        const square = await loadSquareWebPaymentsSdk(sandbox);
        if (cancelled) return;
        const payments = square.payments(applicationId, locationId);
        const card = await payments.card();
        if (cancelled) {
          await card.destroy();
          return;
        }
        await card.attach(`#square-studio-card-${cardContainerId}`);
        if (cancelled) {
          await card.destroy();
          return;
        }
        cardRef.current = card;
        setIsCardReady(true);
      } catch (error) {
        if (cancelled) return;
        reportError(error, {
          featureArea: 'payment_method_setup',
          parentId,
        });
        setLoadError('Could not load the Square payment form');
      }
    };

    void attachCard();

    return () => {
      cancelled = true;
      if (cardRef.current) {
        void cardRef.current.destroy().catch(() => undefined);
        cardRef.current = null;
      }
    };
  }, [
    squareConfig?.applicationId,
    squareConfig?.locationId,
    squareConfig?.sandbox,
    cardContainerId,
    parentId,
  ]);

  const handleSubmit = async () => {
    if (submitLockRef.current) return;
    const card = cardRef.current;
    const config = setupQuery.data?.square;
    if (!card || !config) return;

    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      // Studio staff enters the card for the parent (seller-keyed / MOTO).
      // Square requires STORE intent on tokenize; do not use deprecated verifyBuyer.
      const tokenResult = await card.tokenize(
        buildSellerKeyedStoreVerificationDetails({
          email: parentEmail?.trim() || undefined,
        }),
      );
      if (tokenResult.status !== 'OK' || !tokenResult.token) {
        toast.error(
          tokenResult.errors?.[0]?.message ?? 'Could not verify this card',
        );
        return;
      }

      await createCard.mutateAsync({
        parentId,
        sourceId: tokenResult.token,
      });
      toast.success('Payment method saved');
      onSuccess();
    } catch (error) {
      reportError(error, { featureArea: 'payment_method_setup', parentId });
      toast.error(getErrorMessage(error, 'Failed to save payment method'));
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  if (setupQuery.isError || loadError) {
    return (
      <div className='space-y-4 py-4 text-center'>
        <p className='text-sm font-medium'>
          {loadError ?? 'Could not start Square payment setup'}
        </p>
        <DialogFooter>
          <Button type='button' variant='outline' onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void handleSubmit();
      }}
      className='space-y-4'
    >
      <div
        id={`square-studio-card-${cardContainerId}`}
        className='min-h-[90px] rounded-md border p-3'
      />
      {!isCardReady && (
        <div className='flex items-center justify-center gap-2 py-2 text-muted-foreground'>
          <Loader2Icon className='size-6 animate-spin' />
          <span>Preparing secure form…</span>
        </div>
      )}
      <DialogFooter className='gap-2 sm:gap-0'>
        <Button type='button' variant='outline' onClick={onClose}>
          Cancel
        </Button>
        <Button type='submit' disabled={isSubmitting || !isCardReady}>
          {isSubmitting ? (
            <Loader2Icon className='size-4 animate-spin' />
          ) : (
            'Save payment method'
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};
